import React, { createContext, useContext, useMemo } from 'react'
import type { TourConfig, TourCompletionRecord, TourStateAdapter } from './types'
import { hasSeenTour, markTourSeen, readTourRecord } from './tourStorage'

/**
 * The fallback: completion state in localStorage, exactly as `Tour` handled it before this context
 * existed. It is per-browser and it does not survive a logout, which is why an app mounts a
 * provider over it. Storybook and the Playwright harness rely on this default.
 */
const localStorageAdapter: TourStateAdapter = {
  read: readTourRecord,
  write: (tourId, record) => markTourSeen(tourId, { version: record.version, dismissed: record.dismissed })
}

const TourContext = createContext<TourConfig>({})

export interface TourProviderProps extends TourConfig {
  children: React.ReactNode
}

/**
 * Supplies every `Tour` below it with somewhere to keep completion state and somewhere to send
 * analytics events.
 *
 * Mount it once, near the root. The point is that a new tour is persisted and instrumented because
 * it is in the tree, not because somebody remembered to wire it up.
 */
export function TourProvider({ state, onEvent, children }: TourProviderProps): JSX.Element {
  const value = useMemo<TourConfig>(() => ({ state, onEvent }), [state, onEvent])
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

/** The state adapter in scope, falling back to localStorage when no provider is mounted. */
export function useTourState(): TourStateAdapter {
  const { state } = useContext(TourContext)
  return state ?? localStorageAdapter
}

/** The analytics sink in scope. Undefined when nothing is listening. */
export function useTourEvents(): TourConfig['onEvent'] {
  return useContext(TourContext).onEvent
}

/**
 * Whether a one-time tour has already been finished or dismissed at this version or newer.
 *
 * A dismissal counts. A user who closes a tour deliberately should not have it open itself again,
 * and every tour we ship can be replayed from a button, so nothing is lost by respecting that.
 */
export function hasSeenTourIn(state: TourStateAdapter, tourId: string, version: number): boolean {
  // Keep the localStorage path on its own helper, which already reads and validates the record.
  if (state === localStorageAdapter) return hasSeenTour(tourId, version)

  const record = state.read(tourId)
  return !!record && typeof record.version === 'number' && record.version >= version
}

export type { TourCompletionRecord }
export default TourContext
